//! Cloned from pixi-viewport Wheel plugin.

import { events } from '@/app/events/events';
import { BORDER_VISIBILITY_OFFSET, getViewportClampConfig } from '@/app/gridGL/pixiApp/viewport/viewportClamp';
import { CELL_HEIGHT } from '@/shared/constants/gridConstants';
import { isMac } from '@/shared/utils/isMac';
import type { Viewport } from 'pixi-viewport';
import { Plugin } from 'pixi-viewport';
import type { IPointData } from 'pixi.js';
import { Point } from 'pixi.js';

/**
 * Detect if we need Safari-style gesture event handling for pinch-to-zoom.
 * Safari on macOS fires GestureEvents instead of wheel events with
 * ctrlKey for trackpad pinch gestures.
 *
 * We detect this by checking if GestureEvent is available on the window,
 * which is a Safari-specific API. This works regardless of user agent
 * string.
 */
const needsSafariGestureHandling = (): boolean => {
  // GestureEvent is Safari/WKWebView-specific for trackpad pinch gestures
  const hasGestureEvent = typeof window !== 'undefined' && 'GestureEvent' in window;
  return hasGestureEvent;
};

/** Safari-specific GestureEvent for trackpad pinch gestures */
interface SafariGestureEvent extends UIEvent {
  scale: number;
  clientX: number;
  clientY: number;
}

export const SCALE_OUT_OF_BOUNDS_SCROLL = 0.1;

const MAX_RUBBER_BAND_GAP = 0;
const RUBBER_BAND_DECELERATION_POWER = 5;

/** Options for {@link Wheel}. */
export interface IWheelOptions {
  /**
   * Percent to scroll with each spin
   *
   * @default 0.1
   */
  percent?: number;

  /**
   * smooth the zooming by providing the number of frames to zoom between wheel spins
   *
   * @default false
   */
  smooth?: false | number;

  /**
   * Stop smoothing with any user input on the viewport
   *
   * @default true
   */
  interrupt?: boolean;

  /**
   * Reverse the direction of the scroll
   *
   * @default false
   */
  reverse?: boolean;

  /**
   * Place this point at center during zoom instead of current mouse position
   *
   * @default null
   */
  center?: Point | null;

  /**
   * Scaling factor for non-DOM_DELTA_PIXEL scrolling events
   *
   * @default 20
   */
  lineHeight?: number;

  /**
   * Axis to zoom
   *
   * @default 'all'
   */
  axis?: 'all' | 'x' | 'y';

  /**
   * Array containing {@link key|https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code} codes of
   * keys that can be pressed for the zoom to be triggered, e.g.: ['ShiftLeft', 'ShiftRight'}.
   *
   * @default null
   */
  keyToPress?: string[] | null;

  /**
   * pinch the trackpad to zoom
   */
  trackpadPinch?: boolean;

  /**
   * zooms on wheel spin (use this as an alternative to drag.options.wheel)
   */
  wheelZoom?: boolean;
}

export const ZOOM_KEY = ['ControlKey', 'ControlLeft', 'ControlRight', 'MetaKey', 'MetaLeft', 'MetaRight'];

export const HORIZONTAL_SCROLL_KEY = ['ShiftLeft', 'ShiftRight'];

const DEFAULT_WHEEL_OPTIONS: Required<IWheelOptions> = {
  percent: 0.1,
  smooth: false,
  interrupt: true,
  reverse: false,
  center: null,
  lineHeight: 20,
  axis: 'all',
  keyToPress: null,
  trackpadPinch: false,
  wheelZoom: true,
};

/**
 * Plugin for handling wheel scrolling for viewport zoom.
 *
 * @event wheel({wheel: {dx, dy, dz}, event, viewport})
 */
export class Wheel extends Plugin {
  public readonly options: Required<IWheelOptions>;

  protected smoothing?: IPointData | null;
  protected smoothingCenter?: Point | null;
  protected smoothingCount?: number;

  /** Flags whether the keys required to zoom are pressed currently. */
  protected zoomKeyIsPressed: boolean;

  /** Flags whether the keys required to horizontal scrolling are currently pressed. */
  protected horizontalScrollKeyIsPressed: boolean;

  /** Flags whether the viewport is currently zooming using ctrl key + wheel. */
  protected currentlyZooming: boolean;

  protected headingSize: { width: number; height: number } = { width: CELL_HEIGHT, height: CELL_HEIGHT };

  /** Safari gesture tracking: scale at last gesture event */
  protected lastGestureScale: number = 1;

  /** Safari gesture tracking: last pointer position during gesture */
  protected gestureCenter: Point = new Point();

  /** Safari gesture tracking: whether the current gesture started on the canvas */
  protected gestureStartedOnCanvas: boolean = false;

  /** Whether the keydown/keyup listeners were registered (only when `keyToPress` is set). */
  private keyListenersAdded: boolean = false;

  /**
   * This is called by {@link Viewport.wheel}.
   */
  constructor(parent: Viewport, options: IWheelOptions = {}) {
    super(parent);
    this.options = Object.assign({}, DEFAULT_WHEEL_OPTIONS, options);
    this.zoomKeyIsPressed = false;
    this.horizontalScrollKeyIsPressed = false;
    this.currentlyZooming = false;
    if (this.options.keyToPress) {
      this.handleKeyPresses(this.options.keyToPress);
    }
    window.addEventListener('blur', this.handleBlur);
    window.addEventListener('pointerup', this.checkAndEmitZoomEnd);
    window.addEventListener('pointerdown', this.checkAndEmitZoomEnd);
    window.addEventListener('pointerout', this.checkAndEmitZoomEnd);
    window.addEventListener('pointerleave', this.checkAndEmitZoomEnd);

    events.on('headingSize', this.handleHeadingSize);

    if (needsSafariGestureHandling() && this.options.trackpadPinch) {
      this.setupSafariGestureHandlers();
    }
  }

  /** Removes every document/window listener registered by this plugin. Called by {@link PluginManager} when the plugin is removed. */
  public destroy(): void {
    window.removeEventListener('blur', this.handleBlur);
    window.removeEventListener('pointerup', this.checkAndEmitZoomEnd);
    window.removeEventListener('pointerdown', this.checkAndEmitZoomEnd);
    window.removeEventListener('pointerout', this.checkAndEmitZoomEnd);
    window.removeEventListener('pointerleave', this.checkAndEmitZoomEnd);
    if (this.keyListenersAdded) {
      window.removeEventListener('keydown', this.handleKeyDown);
      window.removeEventListener('keyup', this.handleKeyUp);
    }
    events.off('headingSize', this.handleHeadingSize);
    this.cleanupSafariGestureHandlers();
  }

  private handleHeadingSize = (width: number, height: number): void => {
    this.headingSize = { width, height };
  };

  /** Set up Safari-specific gesture event handlers for pinch-to-zoom on trackpad */
  private setupSafariGestureHandlers(): void {
    document.addEventListener('gesturestart', this.handleGestureStart as EventListener, { passive: false });
    document.addEventListener('gesturechange', this.handleGestureChange as EventListener, { passive: false });
    document.addEventListener('gestureend', this.handleGestureEnd as EventListener, { passive: false });
  }

  /** Remove Safari-specific gesture event handlers added by {@link setupSafariGestureHandlers} */
  private cleanupSafariGestureHandlers(): void {
    document.removeEventListener('gesturestart', this.handleGestureStart as EventListener);
    document.removeEventListener('gesturechange', this.handleGestureChange as EventListener);
    document.removeEventListener('gestureend', this.handleGestureEnd as EventListener);
  }

  /** Check if a gesture event is over the canvas element */
  private isGestureOnCanvas(e: SafariGestureEvent): boolean {
    const element = document.elementFromPoint(e.clientX, e.clientY);
    return element?.tagName === 'CANVAS';
  }

  private handleGestureStart = (e: SafariGestureEvent): void => {
    this.gestureStartedOnCanvas = this.isGestureOnCanvas(e);
    if (!this.gestureStartedOnCanvas) return;

    e.preventDefault();
    if (this.paused) return;

    this.lastGestureScale = e.scale;
    this.gestureCenter.set(e.clientX, e.clientY);
    this.parent.emit('pinch-start', { viewport: this.parent });
  };

  private handleGestureChange = (e: SafariGestureEvent): void => {
    if (!this.gestureStartedOnCanvas) return;

    e.preventDefault();
    if (this.paused) return;

    const scaleChange = e.scale / this.lastGestureScale;
    this.lastGestureScale = e.scale;
    this.gestureCenter.set(e.clientX, e.clientY);

    this.applyGesturePinch(scaleChange, this.gestureCenter);
  };

  private handleGestureEnd = (e: SafariGestureEvent): void => {
    if (!this.gestureStartedOnCanvas) return;

    e.preventDefault();
    if (this.paused) return;

    this.lastGestureScale = 1;
    this.gestureStartedOnCanvas = false;
    this.parent.emit('pinch-end', { viewport: this.parent });
  };

  /** Apply a pinch zoom from a Safari gesture event */
  private applyGesturePinch(scaleChange: number, screenPoint: Point): void {
    const point = new Point();
    this.parent.options.events.mapPositionToPoint(point, screenPoint.x, screenPoint.y);

    let oldPoint: IPointData | undefined;
    if (!this.options.center) {
      oldPoint = this.parent.toLocal(point);
    }

    if (this.isAxisX()) {
      this.parent.scale.x *= scaleChange;
    }
    if (this.isAxisY()) {
      this.parent.scale.y *= scaleChange;
    }

    this.parent.emit('zoomed', { viewport: this.parent, type: 'wheel' });

    const clamp = this.parent.plugins.get('clamp-zoom', true);
    if (clamp) {
      clamp.clamp();
    }

    if (this.options.center) {
      this.parent.moveCenter(this.options.center);
    } else {
      const newPoint = this.parent.toGlobal(oldPoint as IPointData);
      this.parent.x += point.x - newPoint.x;
      this.parent.y += point.y - newPoint.y;
    }

    this.parent.emit('moved', { viewport: this.parent, type: 'wheel' });
  }

  private handleBlur = (): void => {
    this.zoomKeyIsPressed = false;
    this.horizontalScrollKeyIsPressed = false;
    this.checkAndEmitZoomEnd();
  };

  /**
   * Handles keypress events and set the keyIsPressed boolean accordingly
   *
   * @param {array} codes - key codes that can be used to trigger zoom event
   */
  protected handleKeyPresses(codes: string[]): void {
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    this.keyListenersAdded = true;
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (this.isZoomKey(e.code)) {
      this.zoomKeyIsPressed = true;
    }
    if (this.isHorizontalScrollKey(e.code)) {
      this.horizontalScrollKeyIsPressed = true;
    }
    this.checkAndEmitZoomEnd();
  };

  private handleKeyUp = (e: KeyboardEvent): void => {
    if (this.isZoomKey(e.code)) {
      this.zoomKeyIsPressed = false;
    }
    if (this.isHorizontalScrollKey(e.code)) {
      this.horizontalScrollKeyIsPressed = false;
    }
    this.checkAndEmitZoomEnd();
  };

  public down(): boolean {
    if (this.options.interrupt) {
      this.smoothing = null;
    }

    return false;
  }

  protected isAxisX(): boolean {
    return ['all', 'x'].includes(this.options.axis);
  }

  protected isAxisY(): boolean {
    return ['all', 'y'].includes(this.options.axis);
  }

  protected isZoomKey(key: string): key is (typeof ZOOM_KEY)[number] {
    return ZOOM_KEY.includes(key);
  }

  protected isHorizontalScrollKey(key: string): key is (typeof HORIZONTAL_SCROLL_KEY)[number] {
    return HORIZONTAL_SCROLL_KEY.includes(key);
  }

  protected emitWaitForZoomEnd = (): void => {
    this.currentlyZooming = true;
    this.parent.emit('wait-for-zoom-end');
  };

  protected checkAndEmitZoomEnd = (): void => {
    if (this.currentlyZooming) {
      this.currentlyZooming = false;
      this.parent.emit('zoom-end');
    }
  };

  public update(): void {
    if (this.smoothing) {
      const point = this.smoothingCenter;
      const change = this.smoothing;
      let oldPoint;

      if (!this.options.center) {
        oldPoint = this.parent.toLocal(point as IPointData);
      }
      if (this.isAxisX()) {
        this.parent.scale.x += change.x;
      }
      if (this.isAxisY()) {
        this.parent.scale.y += change.y;
      }

      this.parent.emit('zoomed', { viewport: this.parent, type: 'wheel' });
      const clamp = this.parent.plugins.get('clamp-zoom', true);

      if (clamp) {
        clamp.clamp();
      }
      if (this.options.center) {
        this.parent.moveCenter(this.options.center);
      } else {
        const newPoint = this.parent.toGlobal(oldPoint as IPointData);

        this.parent.x += (point as IPointData).x - newPoint.x;
        this.parent.y += (point as IPointData).y - newPoint.y;
      }

      this.parent.emit('moved', { viewport: this.parent, type: 'wheel' });
      (this.smoothingCount as number)++;

      if ((this.smoothingCount as number) >= (this.options.smooth as number)) {
        this.smoothing = null;
      }
    }
  }

  // adjust is used to move the event for IFrames
  private getPointerPosition(e: WheelEvent, adjust?: { x: number; y: number }) {
    const point = new Point();
    this.parent.options.events.mapPositionToPoint(
      point,
      e.clientX + (adjust ? adjust.x : 0),
      e.clientY + (adjust ? adjust.y : 0)
    );
    return point;
  }

  private pinch(e: WheelEvent, adjust?: { x: number; y: number }) {
    if (this.paused) {
      return;
    }

    const point = this.getPointerPosition(e, adjust);
    const step = (-e.deltaY * (e.deltaMode ? this.options.lineHeight : 1)) / 200;
    const change = Math.pow(2, (1 + this.options.percent) * step);

    let oldPoint: IPointData | undefined;

    if (!this.options.center) {
      oldPoint = this.parent.toLocal(point);
    }
    if (this.isAxisX()) {
      this.parent.scale.x *= change;
    }
    if (this.isAxisY()) {
      this.parent.scale.y *= change;
    }
    this.parent.emit('zoomed', { viewport: this.parent, type: 'wheel' });
    const clamp = this.parent.plugins.get('clamp-zoom', true);

    if (clamp) {
      clamp.clamp();
    }
    if (this.options.center) {
      this.parent.moveCenter(this.options.center);
    } else {
      const newPoint = this.parent.toGlobal(oldPoint as IPointData);

      this.parent.x += point.x - newPoint.x;
      this.parent.y += point.y - newPoint.y;
    }

    this.parent.emit('moved', { viewport: this.parent, type: 'wheel' });
    this.parent.emit('wheel-start', {
      event: e,
      viewport: this.parent,
    });
  }

  // This is a hack to double check that the zoom key is pressed. This fixes a
  // bug where the zoom key is pressed when on the app, but the keyup is not
  // caught b/c an external program is called (eg, taking a screen shot with
  // meta+4)
  private doubleCheckZoomKey(e: WheelEvent) {
    if (this.zoomKeyIsPressed && !e.ctrlKey && !e.metaKey) {
      this.zoomKeyIsPressed = false;
    }
  }

  public wheel(e: WheelEvent, adjust?: { x: number; y: number }): boolean {
    this.doubleCheckZoomKey(e);

    // If paused or both zoom and horizontal keys are pressed do nothing
    if (this.paused || (this.zoomKeyIsPressed && this.horizontalScrollKeyIsPressed)) {
      return false;
    }

    // If this is a trackpad pinch event (e.ctrlKey is true) but trackpadPinch is disabled,
    // ignore the event entirely to prevent pinch gestures from being treated as scroll
    if (e.ctrlKey && !this.options.trackpadPinch && !this.zoomKeyIsPressed) {
      return false;
    }

    // If zoom key is pressed but wheelZoom is disabled, ignore the event
    if (this.zoomKeyIsPressed && !this.options.wheelZoom) {
      return false;
    }

    if (this.zoomKeyIsPressed) {
      const point = this.getPointerPosition(e);
      const sign = this.options.reverse ? -1 : 1;
      const step = (sign * -e.deltaY * (e.deltaMode ? this.options.lineHeight : 1)) / 500;
      const change = Math.pow(2, (1 + this.options.percent) * step);

      if (this.options.smooth) {
        const original = {
          x: this.smoothing ? this.smoothing.x * (this.options.smooth - (this.smoothingCount as number)) : 0,
          y: this.smoothing ? this.smoothing.y * (this.options.smooth - (this.smoothingCount as number)) : 0,
        };

        this.smoothing = {
          x: ((this.parent.scale.x + original.x) * change - this.parent.scale.x) / this.options.smooth,
          y: ((this.parent.scale.y + original.y) * change - this.parent.scale.y) / this.options.smooth,
        };
        this.smoothingCount = 0;
        this.smoothingCenter = point;
      } else {
        let oldPoint: IPointData | undefined;

        if (!this.options.center) {
          oldPoint = this.parent.toLocal(point);
        }
        if (this.isAxisX()) {
          this.parent.scale.x *= change;
        }
        if (this.isAxisY()) {
          this.parent.scale.y *= change;
        }
        this.parent.emit('zoomed', { viewport: this.parent, type: 'wheel' });
        this.emitWaitForZoomEnd();
        const clamp = this.parent.plugins.get('clamp-zoom', true);

        if (clamp) {
          clamp.clamp();
        }
        if (this.options.center) {
          this.parent.moveCenter(this.options.center);
        } else {
          const newPoint = this.parent.toGlobal(oldPoint as IPointData);

          this.parent.x += point.x - newPoint.x;
          this.parent.y += point.y - newPoint.y;
        }
      }

      this.parent.emit('moved', { viewport: this.parent, type: 'wheel' });
      this.parent.emit('wheel-start', {
        wheel: { dx: e.deltaX, dy: e.deltaY, dz: e.deltaZ },
        event: e,
        viewport: this.parent,
      });
    } else if (e.ctrlKey && this.options.trackpadPinch) {
      this.pinch(e, adjust);
    } else {
      const deltas = [e.deltaX, e.deltaY];
      const [deltaX, deltaY] = deltas;

      const { x: viewportX, y: viewportY } = this.parent;

      const { contentBounds, clampVisibleToContentEdges, leftClamp, topClamp } = getViewportClampConfig(
        this.headingSize
      );

      const stepX = deltaX < 0 && viewportX > 0 ? SCALE_OUT_OF_BOUNDS_SCROLL : 1;
      const stepY = deltaY < 0 && viewportY > 0 ? SCALE_OUT_OF_BOUNDS_SCROLL : 1;

      // Calculate the scroll amount
      let dx = (this.horizontalScrollKeyIsPressed && !isMac ? deltaY : deltaX) * stepX * -1;
      let dy = (this.horizontalScrollKeyIsPressed && !isMac ? 0 : deltaY) * stepY * -1;

      // Calculate actual position of the viewport after scrolling (relative to left clamp)
      const nextX = viewportX + dx - leftClamp;
      const nextY = viewportY + dy - topClamp;

      if (viewportX >= leftClamp) {
        // going beyond the left clamp, decelerate
        const factorX = this.getDecelerationFactor(nextX);
        dx *= factorX;
      } else if (nextX > 0) {
        // snap to the edge
        dx -= nextX;
      }

      if (viewportY >= topClamp) {
        // going beyond the top clamp, decelerate
        const factorY = this.getDecelerationFactor(nextY);
        dy *= factorY;
      } else if (nextY > 0) {
        // snap to the edge
        dy -= nextY;
      }

      let finalX = viewportX + dx;
      let finalY = viewportY + dy;

      // Hard clamp to content bounds (right/bottom boundaries) when enabled
      if (clampVisibleToContentEdges && contentBounds) {
        const visibleWidth = this.parent.screenWidth / this.parent.scale.x;
        const visibleHeight = this.parent.screenHeight / this.parent.scale.y;

        // If content is wider than visible area, clamp to align right edges
        // If content is smaller, lock at left edge of range
        if (contentBounds.width > visibleWidth) {
          const minX = -(contentBounds.right - visibleWidth) * this.parent.scale.x - BORDER_VISIBILITY_OFFSET;
          if (finalX < minX) {
            finalX = minX;
          }
        } else {
          // Content fits in viewport - lock at left edge of range
          if (finalX < leftClamp) {
            finalX = leftClamp;
          }
        }

        // If content is taller than visible area, clamp to align bottom edges
        // If content is smaller, lock at top edge of range
        if (contentBounds.height > visibleHeight) {
          const minY = -(contentBounds.bottom - visibleHeight) * this.parent.scale.y - BORDER_VISIBILITY_OFFSET;
          if (finalY < minY) {
            finalY = minY;
          }
        } else {
          // Content fits in viewport - lock at top edge of range
          if (finalY < topClamp) {
            finalY = topClamp;
          }
        }
      }

      this.parent.x = finalX;
      this.parent.y = finalY;

      this.parent.emit('wheel-scroll', this.parent);
      this.parent.emit('moved', { viewport: this.parent, type: 'wheel' });
    }
    return !this.parent.options.passiveWheel;
  }

  private getDecelerationFactor(value: number): number {
    // No deceleration needed if the value is less than or equal to 0
    if (value <= 0) return 1;

    // Normalize the gap to be between 0 and 1
    const normalizedGap = Math.min(value / MAX_RUBBER_BAND_GAP, 1);

    // Calculate the deceleration factor using a power function
    const factor = Math.pow(1 - normalizedGap, RUBBER_BAND_DECELERATION_POWER);

    return factor;
  }
}
